import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Users, FileText, Calendar, Globe, Shield, Clock, Phone, Briefcase, GraduationCap, Heart, Plane, Building, Flag, MapPin, Search, Target, Send, Plus, AlertCircle, DollarSign, TrendingUp, Award, HelpCircle, Scale, X, CreditCard, Star, Home } from 'lucide-react';

const UAE = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
    viewport: { once: true }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <motion.section 
        className="bg-custom-pattern py-16 px-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-inter font-bold md:font-black text-acBlack leading-tight mb-6">
            United Arab Emirates (UAE)
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Practical guidance for employment, investors and founders, study, family, visits, and long term residence.
          </p>
          
          <div className="bg-white rounded-lg shadow-lg p-8 text-left max-w-3xl mx-auto">
            <h2 className="text-2xl font-semibold text-acBlack mb-4">Snapshot</h2>
            <p className="text-gray-600 leading-relaxed">
              We guide individuals and families through UAE immigration from first entry to residence cards and renewals. We cover employer sponsored residence, free zone and mainland routes, investor and partner visas, Golden Visa and Green Visa categories, student and family routes, and visits. Typical response time is 24 to 72 hours. London covers EMEA. New York runs an APAC desk with evening ET overlap.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Who this is for Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Who this is for
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Professionals</h3>
              <p className="text-gray-600 text-sm">Professionals with a UAE job offer in a free zone or on the mainland</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Building className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Investors & Founders</h3>
              <p className="text-gray-600 text-sm">Investors, founders, and partners who will own or manage a UAE company</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Skilled Candidates</h3>
              <p className="text-gray-600 text-sm">Skilled employees, freelancers, and self employed candidates who may qualify for Green Visa or Golden Visa</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Students</h3>
              <p className="text-gray-600 text-sm">Students enrolling at a UAE university or school</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Family Members</h3>
              <p className="text-gray-600 text-sm">Spouses, partners, children, and in limited cases parents joining a resident</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Plane className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Visitors</h3>
              <p className="text-gray-600 text-sm">Tourists and short term business visitors</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* What we handle at a glance Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            What we handle at a glance
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Employment & Business</h3>
              <p className="text-gray-600 text-sm mb-4">Employer sponsored residence visas in free zones and on the mainland, labour approvals where relevant, status change, medical fitness, Emirates ID biometrics, and residence card issuance</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Investor & Founder Routes</h3>
              <p className="text-gray-600 text-sm mb-4">Partner or investor residence through free zone or mainland company setup, share capital and bank steps, and renewals</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Long Term Residence</h3>
              <p className="text-gray-600 text-sm mb-4">Golden Visa categories for investors, property owners, highly skilled professionals, outstanding talents, researchers, and other qualifying profiles. Green Visa for skilled employees, freelancers, and self employed applicants who meet criteria</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Students</h3>
              <p className="text-gray-600 text-sm mb-4">University sponsored residence and student services</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Family</h3>
              <p className="text-gray-600 text-sm mb-4">Sponsorship for spouse or partner, children, and in limited cases parents where income, accommodation, and insurance rules are met</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Visitors</h3>
              <p className="text-gray-600 text-sm mb-4">Tourist and visit visas, multiple entry options, and extension rules where available</p>
            </div>
          </div>
          <p className="text-center text-gray-600 mt-8">
            We do not handle asylum or removal defense.
          </p>
        </div>
      </motion.section>

      {/* Outcomes Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Outcomes you can expect
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Written Plan</h3>
              <p className="text-gray-600 text-sm">Confirms the correct route, fees, and a realistic timeline</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Document Pack</h3>
              <p className="text-gray-600 text-sm">Document pack that matches attestation and translation rules for the UAE</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Company Setup</h3>
              <p className="text-gray-600 text-sm">Company setup coordination for investor and founder routes where allowed</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Filing Sequence</h3>
              <p className="text-gray-600 text-sm">Filing sequence from entry permit to status change, medical fitness, biometrics, and residence issuance</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Renewal Guidance</h3>
              <p className="text-gray-600 text-sm">Clear rules on renewals, sponsorship responsibilities, and when to convert to a longer term category such as Golden Visa</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Employment and Business Routes Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Employment and business routes
          </h2>
          
          <div className="mb-12">
            <h3 className="text-2xl font-inter font-semibold text-acBlack text-center mb-8">Employer sponsored residence</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Requirements</h4>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Job offer in a free zone or on the mainland with the correct contract and salary level for the position</li>
                  <li>• Quota or labour approvals where required by the jurisdiction</li>
                  <li>• Medical fitness test, Emirates ID biometrics, and health insurance are standard steps</li>
                  <li>• Residence validity typically one to two years depending on zone and contract</li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">What we do</h4>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Map your role and location to the right sponsoring authority and confirm eligibility and salary documentation</li>
                  <li>• Coordinate entry permit, status change in country or out of country, medicals, biometrics, and insurance</li>
                  <li>• Align employer HR steps such as offer letter format, contract registration, and wage protection enrollment where applicable</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Investors, Founders, and Partners Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Investors, founders, and partners
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Company based residence</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Free zone or mainland company formation with ownership and control documented</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Investor or partner visa issuance after establishment card and sponsor registration steps</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Bank account onboarding and office lease coordination where required by the authority</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Golden Visa and Green Visa</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Star className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Assessment for qualifying categories such as investors, property owners, highly skilled professionals, freelancers, and outstanding talents</span>
                </li>
                <li className="flex items-start">
                  <Star className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Thresholds and criteria change over time. We confirm current rules for your profile and emirate and plan evidence accordingly</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-blue-50 p-8 rounded-lg">
            <h3 className="text-xl font-inter font-semibold text-acBlack mb-4">What we do</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Route Comparison</h4>
                <p className="text-gray-600 text-sm">Route comparison across free zones and mainland for cost, processing time, and activity scope</p>
              </div>
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Business Planning</h4>
                <p className="text-gray-600 text-sm">Business plan brief, share capital and ownership documentation, and attestations</p>
              </div>
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Source of Funds</h4>
                <p className="text-gray-600 text-sm">Source of funds and source of wealth evidence planning when relevant to long term residence categories</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Students Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Students
          </h2>
          
          <div className="bg-white p-8 rounded-lg shadow-sm border">
            <h3 className="text-xl font-inter font-semibold text-acBlack mb-6 text-center">University sponsored residence</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Documentation</h4>
                <p className="text-gray-600 text-sm">Offer or enrollment confirmation, tuition and funds evidence, and insurance</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Process</h4>
                <p className="text-gray-600 text-sm">Entry permit, status change, medicals, biometrics, and residence issuance</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Briefcase className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Work Rights</h4>
                <p className="text-gray-600 text-sm">Rules on part time work and on campus permissions vary. We confirm your program's policy</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Family Routes Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Family routes
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Family sponsorship</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Heart className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Sponsorship by a resident who meets income thresholds and accommodation requirements</span>
                </li>
                <li className="flex items-start">
                  <Heart className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Spouse or partner, children, and in limited cases parents may qualify</span>
                </li>
                <li className="flex items-start">
                  <Heart className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Health insurance is typically required</span>
                </li>
                <li className="flex items-start">
                  <Heart className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Marriage, birth, and custody documents often need attestation in the country of issue and UAE legalisation</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">What we do</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Plan sequencing so principal and dependents receive residence with minimal gaps</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Provide templates for sponsor letters, accommodation confirmations, and consent letters for minors</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Coordinate document attestation and translation</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Visitors Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Visitors
          </h2>
          
          <div className="bg-white p-8 rounded-lg shadow-sm border">
            <h3 className="text-xl font-inter font-semibold text-acBlack mb-6 text-center">Tourist and visit visas</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Entry Options</h4>
                <p className="text-gray-600 text-sm">Single or multiple entry options depending on nationality and sponsor</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Documentation</h4>
                <p className="text-gray-600 text-sm">Itinerary planning, sponsor or hotel letters when needed, and extension options where available</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Stay Limits</h4>
                <p className="text-gray-600 text-sm">We confirm current stay limits and entry rules for your passport and purpose</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* End-to-End Process */}
      <motion.section className="py-16 bg-white" {...fadeInUp}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-acBlack mb-4">Our End-to-End Process</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              From initial consultation to final approval, we guide you through every step of your UAE immigration journey.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Initial Consultation", desc: "Comprehensive assessment of your profile and immigration goals", icon: <Users className="w-8 h-8" /> },
              { step: "02", title: "Strategy & Planning", desc: "Tailored immigration strategy with timeline and requirements", icon: <FileText className="w-8 h-8" /> },
              { step: "03", title: "Application Preparation", desc: "Document compilation, form completion, and submission preparation", icon: <CheckCircle className="w-8 h-8" /> },
              { step: "04", title: "Submission & Follow-up", desc: "Application submission and ongoing case management until approval", icon: <Clock className="w-8 h-8" /> }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-blue-600">
                  {item.icon}
                </div>
                <div className="text-2xl font-bold text-blue-600 mb-2">{item.step}</div>
                <h3 className="text-lg font-semibold text-acBlack mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Timelines & Service Levels */}
      <motion.section className="py-16 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-acBlack mb-4">Timelines & Service Levels</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Transparent timelines and flexible service options to meet your specific needs and urgency.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                level: "Standard Service",
                timeline: "4-8 weeks",
                price: "From AED 8,000",
                features: ["Complete application preparation", "Document review & compilation", "Submission & basic follow-up", "Email support"]
              },
              {
                level: "Priority Service",
                timeline: "2-4 weeks",
                price: "From AED 12,000",
                features: ["Expedited processing", "Priority document review", "Enhanced follow-up", "Phone & email support", "Status updates"]
              },
              {
                level: "Premium Service",
                timeline: "1-2 weeks",
                price: "From AED 18,000",
                features: ["Express processing", "Dedicated case manager", "Same-day document review", "24/7 support", "Regular status calls"]
              }
            ].map((service, index) => (
              <div key={index} className={`bg-white rounded-lg p-6 shadow-lg ${index === 1 ? 'border-2 border-blue-400' : ''}`}>
                {index === 1 && <div className="bg-blue-400 text-acBlack text-sm font-semibold px-3 py-1 rounded-full inline-block mb-4">Most Popular</div>}
                <h3 className="text-xl font-bold text-acBlack mb-2">{service.level}</h3>
                <div className="text-2xl font-bold text-blue-600 mb-2">{service.timeline}</div>
                <div className="text-lg font-semibold text-gray-700 mb-4">{service.price}</div>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-gray-600 text-sm">
              * Timelines are estimates based on typical processing times. Actual processing may vary based on individual circumstances and government processing times.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Documents Checklist */}
      <motion.section className="py-16 bg-white" {...fadeInUp}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-acBlack mb-4">Documents Checklist by Category</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive document requirements organized by immigration category to ensure complete applications.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                category: "Employment Visa",
                icon: <Briefcase className="w-6 h-6" />,
                documents: ["Valid passport (6+ months)", "Educational certificates", "Experience certificates", "Medical fitness certificate", "Emirates ID application", "Salary certificate", "Employment contract"]
              },
              {
                category: "Investor Visa",
                icon: <Award className="w-6 h-6" />,
                documents: ["Valid passport", "Bank statements", "Business plan", "Trade license", "Investment proof", "Medical certificate", "Insurance policy", "No objection certificate"]
              },
              {
                category: "Student Visa",
                icon: <GraduationCap className="w-6 h-6" />,
                documents: ["Valid passport", "Educational transcripts", "Admission letter", "Medical certificate", "Financial proof", "Sponsor documents", "Insurance coverage"]
              },
              {
                category: "Family Visa",
                icon: <Heart className="w-6 h-6" />,
                documents: ["Valid passport", "Marriage certificate", "Birth certificates", "Salary certificate", "Tenancy contract", "Medical certificates", "Insurance policies"]
              },
              {
                category: "Golden Visa",
                icon: <Award className="w-6 h-6" />,
                documents: ["Valid passport", "Investment proof", "Property documents", "Bank statements", "Medical certificate", "Insurance policy", "Character certificate"]
              },
              {
                category: "Visit Visa",
                icon: <Plane className="w-6 h-6" />,
                documents: ["Valid passport", "Passport photos", "Application form", "Travel itinerary", "Hotel booking", "Financial proof", "Sponsor documents (if applicable)"]
              }
            ].map((item, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="bg-blue-100 p-2 rounded-lg mr-3 text-blue-600">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-acBlack">{item.category}</h3>
                </div>
                <ul className="space-y-2">
                  {item.documents.map((doc, idx) => (
                    <li key={idx} className="flex items-start text-gray-600 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      {doc}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          <div className="mt-8 bg-blue-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-acBlack mb-2">Important Notes:</h3>
            <ul className="text-gray-600 text-sm space-y-1">
              <li>• All documents must be attested by relevant authorities</li>
              <li>• Arabic translation may be required for certain documents</li>
              <li>• Document requirements may vary based on nationality and specific circumstances</li>
              <li>• We provide a personalized checklist after initial consultation</li>
            </ul>
          </div>
        </div>
      </motion.section>

      {/* Special Situations */}
      <motion.section className="py-16 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-acBlack mb-4">Special Situations We Handle</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Complex cases require specialized expertise. We handle challenging situations with proven strategies.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Visa Rejections",
                desc: "Previous rejection analysis and reapplication strategies",
                icon: <Shield className="w-6 h-6" />
              },
              {
                title: "Document Issues",
                desc: "Missing or problematic documentation solutions",
                icon: <FileText className="w-6 h-6" />
              },
              {
                title: "Status Changes",
                desc: "Converting between different visa categories",
                icon: <Globe className="w-6 h-6" />
              },
              {
                title: "Urgent Applications",
                desc: "Emergency processing for time-sensitive cases",
                icon: <Clock className="w-6 h-6" />
              },
              {
                title: "Complex Profiles",
                desc: "Multiple nationalities, criminal records, medical issues",
                icon: <Users className="w-6 h-6" />
              },
              {
                title: "Business Complications",
                desc: "Complex business structures and investment arrangements",
                icon: <Briefcase className="w-6 h-6" />
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-lg">
                <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4 text-blue-600">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold text-acBlack mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <div className="bg-white rounded-lg p-6 shadow-lg max-w-4xl mx-auto">
              <h3 className="text-xl font-semibold text-acBlack mb-4">Our Success Rate</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">95%</div>
                  <div className="text-gray-600">Overall Success Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">87%</div>
                  <div className="text-gray-600">Rejection Reversals</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">1000+</div>
                  <div className="text-gray-600">Cases Handled</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>


    </div>
  );
};

export default UAE;
